import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { profileService } from '../services/profileService';
import { useToast } from '@contexts/useToast';
import {
  UserProfileForm,
  NotificationPreferencesForm,
  ProfileSettingsState,
  PasswordChangeData,
} from '../types';
import { getApiBaseUrl } from '@utils/apiClient';
import { validatePhoneNumber } from '@utils/phoneValidation';

export const useProfileSettings = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [state, setState] = useState<ProfileSettingsState>({
    profile: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      address: '',
      bio: '',
      language: 'en-US',
      timezone: 'Europe/Berlin',
      dateFormat: 'DD.MM.YYYY',
      avatar: undefined,
    },
    notifications: {
      emailLogin: true,
      emailSecurity: true,
      emailUpdates: true,
      emailMarketing: false,
      smsLogin: true,
      smsSecurity: true,
      smsUpdates: true,
    },
    isLoading: true,
    isSaving: false,
    error: null,
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        // Fetch both profile and notification preferences in parallel
        const [userData, notificationData] = await Promise.all([
          profileService.getCurrentUser(),
          profileService.getNotificationPreferences(),
        ]);

        // Construct full photo URL if photo path is provided
        let photoUrl = userData.photo;
        if (photoUrl && !photoUrl.startsWith('http')) {
          // Handle different photo path formats
          const apiBaseUrl = getApiBaseUrl();
          if (photoUrl.startsWith('/media/')) {
            // For /media/ paths, use the base URL without /api
            photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
          } else if (photoUrl.startsWith('/api/')) {
            // For /api/ paths, remove /api for public access
            photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
          } else {
            // For other relative paths, add the base URL
            photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
          }
        }

        const profileData = {
          id: userData.id.toString(),
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone_number || '', // Backend uses 'phone_number'
          jobTitle: userData.job_title || '',
          address: userData.address || '', // Backend uses 'address'
          bio: userData.description || '', // Backend uses 'description'
          language: userData.language || 'en-US',
          timezone: userData.timezone || 'Europe/Berlin',
          dateFormat: userData.date_format || 'DD.MM.YYYY',
          avatar: photoUrl,
        };

        // Map backend notification field names to frontend field names
        const notificationData_mapped = {
          emailLogin: notificationData.email_login_notifications,
          emailSecurity: notificationData.email_security_alerts,
          emailUpdates: notificationData.email_system_updates,
          emailMarketing: notificationData.email_marketing_communications,
          smsLogin: notificationData.sms_login_notifications,
          smsSecurity: notificationData.sms_security_alerts,
          smsUpdates: notificationData.sms_system_updates,
        };

        setState(prev => ({
          ...prev,
          profile: profileData,
          notifications: notificationData_mapped,
          isLoading: false,
        }));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load profile';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        showToast({
          type: 'error',
          title: 'Profile Load Error',
          message: errorMessage,
          duration: 5000,
        });
      }
    };

    fetchUserData();
  }, [showToast]);

  const updateProfile = (updates: Partial<UserProfileForm>) => {
    setState(prev => ({
      ...prev,
      profile: { ...prev.profile, ...updates },
    }));
  };

  const updateNotifications = (updates: Partial<NotificationPreferencesForm>) => {
    setState(prev => ({
      ...prev,
      notifications: { ...prev.notifications, ...updates },
    }));
  };

  const saveProfile = async () => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    // Validate phone number before saving
    if (state.profile.phone) {
      const phoneValidation = validatePhoneNumber(state.profile.phone, false);
      if (!phoneValidation.isValid) {
        setState(prev => ({
          ...prev,
          isSaving: false,
          error: phoneValidation.error || 'Invalid phone number format',
        }));
        showToast({
          type: 'error',
          title: 'Validation Error',
          message: phoneValidation.error || 'Invalid phone number format',
          duration: 5000,
        });
        return { success: false, error: phoneValidation.error };
      }
    }

    try {
      await profileService.updateUserProfile({
        first_name: state.profile.firstName,
        last_name: state.profile.lastName,
        phone_number: state.profile.phone, // Backend expects 'phone_number'
        job_title: state.profile.jobTitle,
        address: state.profile.address, // Backend uses 'address'
        description: state.profile.bio, // Backend expects 'description'
        timezone: state.profile.timezone,
        language: state.profile.language,
        date_format: state.profile.dateFormat,
      });

      setState(prev => ({ ...prev, isSaving: false }));
      showToast({
        type: 'success',
        title: t('profile.profileUpdated'),
        message: t('profile.profileUpdatedMessage'),
        duration: 5000,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save profile';
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      return { success: false, error: errorMessage };
    }
  };

  const saveNotifications = async () => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      await profileService.updateNotificationPreferences({
        email_login_notifications: state.notifications.emailLogin,
        email_security_alerts: state.notifications.emailSecurity,
        email_system_updates: state.notifications.emailUpdates,
        email_marketing_communications: state.notifications.emailMarketing,
        sms_login_notifications: state.notifications.smsLogin,
        sms_security_alerts: state.notifications.smsSecurity,
        sms_system_updates: state.notifications.smsUpdates,
      });

      setState(prev => ({ ...prev, isSaving: false }));
      showToast({
        type: 'success',
        title: t('profile.notificationsUpdated'),
        message: t('profile.notificationsUpdatedMessage'),
        duration: 5000,
      });
      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save notification preferences';
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (passwordData: PasswordChangeData) => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      await profileService.changePassword(passwordData);

      setState(prev => ({ ...prev, isSaving: false }));
      showToast({
        type: 'success',
        title: 'Password Changed',
        message: 'Your password has been successfully updated.',
        duration: 5000,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      return { success: false, error: errorMessage };
    }
  };

  const uploadAvatar = async (file: File) => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      await profileService.updateUserPhoto(file);

      // Re-fetch current user data to get the updated photo path
      const updatedUserData = await profileService.getCurrentUser();

      // Construct full photo URL from the updated user data
      let photoUrl = updatedUserData.photo;
      if (photoUrl && !photoUrl.startsWith('http')) {
        // Handle different photo path formats
        const apiBaseUrl = getApiBaseUrl();
        if (photoUrl.startsWith('/media/')) {
          // For /media/ paths, use the base URL without /api
          photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
        } else if (photoUrl.startsWith('/api/')) {
          // For /api/ paths, remove /api for public access
          photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
        } else {
          // For other relative paths, add the base URL
          photoUrl = `${apiBaseUrl.replace('/api', '')}${photoUrl}`;
        }
      }

      // Add timestamp to force image reload and bypass cache
      const timestamp = new Date().getTime();
      photoUrl = `${photoUrl}?t=${timestamp}`;

      // Update profile state directly with the new photo URL
      setState(prev => ({
        ...prev,
        profile: { ...prev.profile, avatar: photoUrl },
        isSaving: false,
      }));

      showToast({
        type: 'success',
        title: 'Photo Updated',
        message: 'Your profile photo has been successfully updated.',
        duration: 5000,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload avatar';
      console.error('Avatar upload error:', error);
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      showToast({
        type: 'error',
        title: 'Upload Failed',
        message: errorMessage,
        duration: 5000,
      });
      return { success: false, error: errorMessage };
    }
  };

  const removeAvatar = () => {
    updateProfile({ avatar: undefined });
  };

  return {
    ...state,
    updateProfile,
    updateNotifications,
    saveProfile,
    saveNotifications,
    changePassword,
    uploadAvatar,
    removeAvatar,
  };
};
